<template>
  <div class="config-creation">
    <v-card>
      <v-card-title>{{ page.title }}</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <span v-if="id"><strong>Site :</strong> {{ page.site.name || "Tous" }}</span>
            <site-filter
              v-else
              siteListGetter="aiFactory/getSiteConnectors"
              itemValue="site.id"
              itemText="site.name"
              :initialValue="page.frequency.site_id"
              @valueChanged="updateSite"
              :noUrl="true"
              :multiple="false"
              :dependsOnSiteGroups="false"
              :zindex="zIndex.modale"
            />
          </v-col>
          <v-col cols="9">
            <v-text-field
              label="Nom de la fréquence"
              v-model="page.frequency.name"
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="4">
            <v-select
              v-model="page.time.hour"
              :items="availableHours"
              label="Heure"
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="4">
            <v-select
              v-model="page.time.minutes"
              :items="availableMinutes"
              label="Minutes"
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="4">
            <v-btn
              class="mx-2"
              elevation="0"
              fab
              small
              @click="addTime()"
            >
              <v-icon color="primary">
                {{ icons.mdiPlusCircle }}
              </v-icon>
            </v-btn>
          </v-col>
          <v-col cols="12">
            <v-data-table
              class="ml-6 mr-6"
              :items="page.frequency.times"
              :headers="[
                { text: 'Heure', value: 'hour' },
                { text: 'Minutes', value: 'minutes' },
                { text: 'Suppr.', value: 'edit' },
              ]"
              disable-sort
              hide-default-footer
              disable-pagination
              disable-filtering
            >
              <template v-slot:item.minutes="{ item }">
                {{ addTimeLeadingZero(item.minutes) }}
              </template>
              <template v-slot:item.edit="{ item }">
                <div>
                  <v-btn
                    class="mx-2"
                    elevation="0"
                    fab
                    small
                    @click="removeTime(item)"
                  >
                    <v-icon color="secondary">
                      {{ icons.mdiTrashCan }}
                    </v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-row justify="center" class="pt-6 pb-6">
          <v-btn
            class="ml-6 mr-6 secondary"
            @click="saveFrequency"
            :disabled="saveDisabled"
            >Enregistrer</v-btn
          >
          <v-btn class="ml-6 mr-6 primary" @click="cancelFrequency">Annuler</v-btn>
        </v-row>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import orderBy from "lodash/orderBy";
import uniq from "lodash/uniq";
import { computed, onMounted, reactive, watch } from "@vue/composition-api";
import {
  mdiPlusCircle,
  mdiTrashCan,
} from "@mdi/js";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";

import SiteFilter from "@/components/common/filters/SiteFilter";
import { zIndex } from "@/utils/constants";
import { addTimeLeadingZero } from "@/utils/formatting";

export default {
  name: "FrequencyCreation",
  components: {
    SiteFilter,
  },
  props: {
    id: {
      type: Number,
      validator: (value) => value > 0,
    },
    processType: {
      type: String,
      default: null,
    },
  },
  setup(props, { emit }) {
    const { axiosGet, axiosPost, axiosPut } = useAxios();
    const { store } = useProxy();

    const page = reactive({
      title: props.id ? "Paramétrage de la fréquence" : "Paramétrage de la nouvelle fréquence",
      time: {
        hour: undefined,
        minutes: undefined,
      },
      site: {
        id: 0,
        name: 0,
      },
      frequency: {
        name: "",
        site_id: 0,
        times: [],
      },
    });

    const hours = [
      0, 
      1, 
      2, 
      3, 
      4, 
      5, 
      6, 
      7, 
      8, 
      9, 
      10,
      11, 
      12, 
      13, 
      14, 
      15, 
      16, 
      17, 
      18, 
      19, 
      20,
      21, 
      22, 
      23, 
      24,
    ];
    const minutes = [0, 20, 40];

    // =>  METHODS
    const updateSite = (value) => {
      page.frequency.site_id = value;
    };

    const addTime = () => {
      if (page.time.hour === undefined || 
          page.time.minutes === undefined || 
          page.frequency.times.some(
            time => time.hour === page.time.hour && time.minutes === page.time.minutes
          )) {
        return;
      }

      page.frequency.times.push({ ...page.time });
      page.frequency.times = orderBy(page.frequency.times, ['hour', 'minutes'], ['asc', 'asc']);
    };

    const removeTime = (value) => {
      page.frequency.times = page.frequency.times.filter(
        time => time.hour !== value.hour || time.minutes !== value.minutes
      );
    };

    const cancelFrequency = async () => {
      page.frequency = {
        name: "",
        site_id: 0,
        times: [],
      }

      emit("close-dialog");
    };

    const saveFrequency = async () => {
      const params = {
        name: page.frequency.name,
        site_id: page.frequency.site_id,
        values_str: page.frequency.times.map(
          t => `${addTimeLeadingZero(t.hour)}:${addTimeLeadingZero(t.minutes)}`
        ).join(","),
        ...(props.processType ? { process_type: props.processType } : {}),
      };

      if (props.id) {
        await axiosPut(`/aif-auto-configs/frequency/${props.id}`, params);
      } else {
        await axiosPost("/aif-auto-configs/frequency/", params);
      }
      
      emit("saved");
      emit("close-dialog");
    };

    // =>  COMPUTED
    const saveDisabled = computed(
      () =>
        page.frequency.name.length < 3 ||
        !page.frequency.site_id ||
        page.frequency.times.length === 0
    );

    const availableHours = computed(() => {
      const selectedHours = uniq(page.frequency.times.map(time => time.hour));
      const blockedHours = selectedHours.reduce((acc, h) => {
        const blocked = minutes.every(m => {
          const minute_blocked = page.frequency.times.find(
            time => time.hour === h && time.minutes === m
          );

          return !!minute_blocked;
        });
        if (blocked) {
          return [ ...acc, h];
        }

        return acc;
      }, []);


      return hours.filter(
        h => !blockedHours.includes(h)
      ).map(
        h => ({ text: addTimeLeadingZero(h), value: h })
      );
    });

    const availableMinutes = computed(() => {
      if (page.time.hour === undefined) {
        return minutes.map(m => ({ text: addTimeLeadingZero(m), value: m }));
      }

      return minutes.reduce((acc, m) => {
        if (page.frequency.times.some(time => time.hour === page.time.hour && time.minutes === m)) {
          return acc;
        }

        return [ ...acc, m ];
      }, []).map(m => ({ text: addTimeLeadingZero(m), value: m }));
    });

    // =>  WATCH
    watch(() => props.id, async (newValue) => {
      if (!newValue) {
        return;
      }

      const { data: { name, site_id, values } } = await axiosGet(
        `/aif-auto-configs/frequency/${newValue}`
      );

      page.frequency = {
        name,
        site_id,
        times: values,
      };
    });

    watch(() => page.frequency.site_id, () => {
      const site = (store.getters["common/getSiteList"] ?? []).find(
        s => s.id === page.frequency.site_id
      );

      page.site = site ?? {
        id: 0,
        name: '',
      };
    });

    // =>  HOOKS
    onMounted(async () => {
      if (props.id) {
        await store.dispatch("common/getSiteList");
  
        const { data: { name, site_id, values } } = await axiosGet(
          `/aif-auto-configs/frequency/${props.id}`
        );

        page.frequency = {
          name,
          site_id,
          times: values,
        };
      }
    });

    // =>  DATA
    return {
      addTimeLeadingZero,
      addTime,
      availableHours,
      availableMinutes,
      cancelFrequency,
      icons: {
        mdiPlusCircle,
        mdiTrashCan,
      },
      page,
      removeTime,
      saveDisabled,
      saveFrequency,
      updateSite,
      zIndex,
    };
  },
};
</script>

<style scoped>
.config-creation {
  max-width: 1000px;
  margin: 0 auto;
}
</style>
